import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useState, useTransition } from "react";
import { useLazyLoadQuery } from "react-relay";
import { InternPrivacyWebhooksEventsQuery } from "./__generated__/InternPrivacyWebhooksEventsQuery.graphql";
import { InternPrivacyWebhooksStatsQuery } from "./__generated__/InternPrivacyWebhooksStatsQuery.graphql";

const eventsQuery = graphql`
  query InternPrivacyWebhooksEventsQuery(
    $status: String
    $topic: String
    $limit: Int!
    $offset: Int!
  ) {
    internPrivacyWebhookEvents(
      status: $status
      topic: $topic
      limit: $limit
      offset: $offset
    ) {
      events {
        id
        shopifyEventId
        topic
        shopDomain
        brandName
        customerEmailMasked
        status
        errorMessage
        createdAt
        completedAt
      }
      totalCount
      hasNextPage
      hasPreviousPage
    }
  }
`;

const statsQuery = graphql`
  query InternPrivacyWebhooksStatsQuery($days: Int!) {
    internPrivacyWebhookStats(days: $days) {
      total
      pending
      processing
      completed
      failed
      successRate
    }
  }
`;

const STATUS_COLORS: Record<
  string,
  "default" | "warning" | "success" | "error" | "info"
> = {
  pending: "default",
  processing: "info",
  completed: "success",
  failed: "error",
};

const TOPIC_LABELS: Record<string, string> = {
  "customers/data_request": "Data Request",
  "customers/redact": "Customer Redact",
  "shop/redact": "Shop Redact",
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const InternPrivacyWebhooks = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [topicFilter, setTopicFilter] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [, startTransition] = useTransition();
  const [refreshKey, setRefreshKey] = useState(0);

  const eventsData = useLazyLoadQuery<InternPrivacyWebhooksEventsQuery>(
    eventsQuery,
    {
      status: statusFilter || null,
      topic: topicFilter || null,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    { fetchKey: refreshKey, fetchPolicy: "network-only" }
  );

  const statsData = useLazyLoadQuery<InternPrivacyWebhooksStatsQuery>(
    statsQuery,
    { days: 30 },
    { fetchKey: refreshKey, fetchPolicy: "network-only" }
  );

  const events = eventsData.internPrivacyWebhookEvents;
  const stats = statsData.internPrivacyWebhookStats;

  const handleRefresh = useCallback(() => {
    startTransition(() => {
      setRefreshKey((k) => k + 1);
    });
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    startTransition(() => {
      setPage(newPage);
      setRefreshKey((k) => k + 1);
    });
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      startTransition(() => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setRefreshKey((k) => k + 1);
      });
    },
    []
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Privacy Webhooks
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Toggle filters">
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              size="small"
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total (30d)",
            value: stats.total,
            color: "var(--intern-text-primary)",
          },
          { label: "Pending", value: stats.pending, color: "#757575" },
          { label: "Processing", value: stats.processing, color: "#1976d2" },
          { label: "Completed", value: stats.completed, color: "#2e7d32" },
          { label: "Failed", value: stats.failed, color: "#d32f2f" },
        ].map((stat) => (
          <Grid item xs={6} sm={4} md key={stat.label}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: "center",
                borderColor: "var(--intern-border-default)",
                bgcolor: "var(--intern-bg-primary)",
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: stat.color }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--intern-text-secondary)" }}
              >
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      {showFilters && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderColor: "var(--intern-border-default)",
            bgcolor: "var(--intern-bg-primary)",
          }}
        >
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  startTransition(() => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                    setRefreshKey((k) => k + 1);
                  });
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Topic</InputLabel>
              <Select
                value={topicFilter}
                label="Topic"
                onChange={(e) => {
                  startTransition(() => {
                    setTopicFilter(e.target.value);
                    setPage(0);
                    setRefreshKey((k) => k + 1);
                  });
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="customers/data_request">Data Request</MenuItem>
                <MenuItem value="customers/redact">Customer Redact</MenuItem>
                <MenuItem value="shop/redact">Shop Redact</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {/* Events Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderColor: "var(--intern-border-default)",
          bgcolor: "var(--intern-bg-primary)",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Topic</TableCell>
              <TableCell>Shop</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--intern-text-secondary)" }}
                  >
                    No privacy webhook events found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Chip
                      label={TOPIC_LABELS[event.topic] || event.topic}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {event.shopDomain}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                      {event.brandName || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: 180,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {event.customerEmailMasked || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={event.status}
                      size="small"
                      color={STATUS_COLORS[event.status] || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDate(event.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={events.totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default InternPrivacyWebhooks;
